import React, {useEffect, useRef, useState} from 'react';
import Tippy from '@tippyjs/react';
import Pagination from './Pagination';
import 'tippy.js/dist/tippy.css';
import Loader from './Loader';
import {Edit2, Save, Trash, X, ChevronDown, ChevronRight, EllipsisVertical} from 'lucide-react';
import {confirmAction} from "../../utils/sweetAlertUtils";

interface TableColumn {
    label: string | React.ReactNode;
    key: string;
    format?: (value: any, row: any) => string;
    isImage?: boolean;
    imageSize?: string;
    editable?: boolean;
    width?: string;
    render?: (row: any) => JSX.Element;
    error?: (row: any) => string | null;
    className?: string;
}

interface TableAction {
    label: string;
    onClick: (row: any) => void;
    confirmMessage?: string;
    icon?: React.ReactNode;
}

interface TableProps {
    columns: TableColumn[];
    data: any[];
    actions?: {
        onEdit?: (row: any) => void;
        onDelete?: (row: any) => void;
        extra?: TableAction[];
    };
    groupBy?: string[]; // New prop for grouping data
    groupHeaderRender?: (group: string, rows: any[]) => JSX.Element; // Custom group header render
    currentPage: number;
    totalPages: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    isLoading: boolean;
    noDataMessage?: string;
    enableInlineEditing?: boolean;
}

const Table: React.FC<TableProps> = ({
                                         columns,
                                         data,
                                         actions,
                                         groupBy,
                                         groupHeaderRender, // Custom group header render prop
                                         currentPage,
                                         totalPages,
                                         pageSize,
                                         onPageChange,
                                         onPageSizeChange,
                                         isLoading,
                                         noDataMessage = 'No records found',
                                         enableInlineEditing = false,
                                     }) => {
    const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
    const [rowEditIndex, setRowEditIndex] = useState<number | null>(null);
    const [editedRow, setEditedRow] = useState<any>({});
    const [dropdownVisible, setDropdownVisible] = useState<number | null>(null);

    // Group data by specified fields
    const groupedData = groupBy
        ? data.reduce((acc, row) => {
            const groupKey = groupBy.map((key) => row[key]).join(' - ');
            if (!acc[groupKey]) {
                acc[groupKey] = [];
            }
            acc[groupKey].push(row);
            return acc;
        }, {} as Record<string, any[]>)
        : {'': data};

    const toggleGroup = (group: string) => {
        setCollapsedGroups((prev) => ({
            ...prev,
            [group]: !prev[group],
        }));
    };

    const toggleDropdown = (index: number) => {
        setDropdownVisible(dropdownVisible === index ? null : index);
    };

    const closeDropdown = () => {
        setDropdownVisible(null);
    };

    return (
        <div className="overflow-hidden">
            <table className="min-w-full bg-white dark:bg-gray-800 border dark:border-gray-700">
                <thead>
                <tr>
                    {columns.map((col) => (
                        <th
                            key={col.key}
                            style={{width: col.width}}
                            className={`px-4 py-2 border-b bg-gray-50 dark:bg-gray-700 text-left text-sm font-semibold text-gray-700 dark:text-gray-300 ${col.className || ''}`}
                        >
                            {col.label}
                        </th>
                    ))}
                    {actions && (actions.onEdit || actions.onDelete || actions.extra) && (
                        <th className="px-2 py-2 border-b bg-gray-50 dark:bg-gray-700 text-left text-sm font-semibold text-gray-700 dark:text-gray-300 w-24">
                            Actions
                        </th>
                    )}
                </tr>
                </thead>
                <tbody>
                {isLoading ? (
                    <tr>
                        <td colSpan={columns.length + (actions ? 1 : 0)} className="text-center py-4">
                            <Loader/>
                        </td>
                    </tr>
                ) : data.length === 0 ? (
                    <tr>
                        <td
                            colSpan={columns.length + (actions ? 1 : 0)}
                            className="text-center py-4 text-sm text-gray-500 dark:text-gray-400"
                        >
                            {noDataMessage}
                        </td>
                    </tr>
                ) : (
                    Object.entries(groupedData).map(([group, rows]) => {
                        const typedRows = rows as any[]; // Explicit type assertion
                        return (
                            <React.Fragment key={group}>
                                {/* Group Header */}
                                {group && (
                                    <tr
                                        className="bg-gray-100 dark:bg-gray-700 cursor-pointer"
                                        onClick={() => toggleGroup(group)}
                                    >
                                        <td
                                            colSpan={columns.length + (actions ? 1 : 0)}
                                            className="px-0 py-2 font-semibold text-gray-700 dark:text-gray-300"
                                        >
                                            <div className="px-4 flex items-center">
                                                {/* Left Section: Chevron */}
                                                <span className="mr-2">
                                            {collapsedGroups[group] ? (
                                                <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-300"/>
                                            ) : (
                                                <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-300"/>
                                            )}
                                        </span>

                                                {/* Right Section: Full Flex Container */}
                                                <div className="flex flex-1 justify-between items-center">
                                                    {/* Inject Custom Header */}
                                                    {groupHeaderRender && groupHeaderRender(group, typedRows)}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                {/* Group Rows */}
                                {!collapsedGroups[group] &&
                                    typedRows.map((row, idx) => (
                                        <tr
                                            key={row.id || idx}
                                            className="hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                        >
                                            {columns.map((col) => (
                                                <td
                                                    key={col.key}
                                                    style={{width: col.width}}
                                                    className={`px-4 py-2 border-b text-xs text-gray-700 dark:text-gray-300 ${col.className || ''}`}
                                                >
                                                    {col.render ? (
                                                        col.render(row)
                                                    ) : rowEditIndex === idx && col.editable && enableInlineEditing ? (
                                                        <input
                                                            type="text"
                                                            value={editedRow[col.key] || ''}
                                                            onChange={(e) =>
                                                                setEditedRow({
                                                                    ...editedRow,
                                                                    [col.key]: e.target.value,
                                                                })
                                                            }
                                                            className="border px-2 py-1 w-full bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                                        />
                                                    ) : col.isImage ? (
                                                        row[col.key] ? (
                                                            <img
                                                                src={row[col.key]}
                                                                alt=""
                                                                className="rounded-full"
                                                                style={{
                                                                    width: col.imageSize || '40px',
                                                                    height: col.imageSize || '40px',
                                                                }}
                                                            />
                                                        ) : (
                                                            <div
                                                                className="rounded-full bg-gray-300 dark:bg-gray-600 text-white flex items-center justify-center"
                                                                style={{
                                                                    width: col.imageSize || '40px',
                                                                    height: col.imageSize || '40px',
                                                                }}
                                                            >
                                                                ?
                                                            </div>
                                                        )
                                                    ) : col.format ? (
                                                        col.format(row[col.key], row)
                                                    ) : (
                                                        row[col.key]
                                                    )}
                                                </td>
                                            ))}
                                            {actions && (actions.onEdit || actions.onDelete || actions.extra) && (
                                                <td className="px-4 py-2 border-b text-sm text-left">
                                                    <div className="flex space-x-2 justify-start">
                                                        {actions.onEdit && (
                                                            <Tippy content="Edit" >
                                                                <button
                                                                    type="button"
                                                                    onClick={() => actions.onEdit && actions.onEdit(row)}
                                                                    className="text-white bg-customPrimary p-2 hover:bg-opacity-90 transition-colors"
                                                                >
                                                                    <Edit2 className="h-4 w-4"/>
                                                                </button>
                                                            </Tippy>
                                                        )}
                                                        {actions.onDelete && (
                                                            <Tippy content="Delete">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => actions.onDelete && actions.onDelete(row)}
                                                                    className="text-white bg-red-500 p-2 hover:bg-red-600 transition-colors"
                                                                >
                                                                    <Trash className="h-4 w-4"/>
                                                                </button>
                                                            </Tippy>
                                                        )}
                                                        {actions.extra && (
                                                            <div className="relative">
                                                                <button
                                                                    onClick={() => toggleDropdown(idx)}
                                                                    className="text-gray-700 bg-gray-200 p-2 rounded hover:bg-gray-300"
                                                                >
                                                                    <EllipsisVertical className="h-4 w-4"/>
                                                                </button>
                                                                {dropdownVisible === idx && (
                                                                    <div
                                                                        className="absolute right-0 mt-2 w-40 bg-white shadow-lg z-50 rounded-md overflow-hidden"
                                                                        onClick={closeDropdown}
                                                                    >
                                                                        {actions.extra.map((action, index) => (
                                                                            <button
                                                                                key={index}
                                                                                onClick={async () => {
                                                                                    if (action.confirmMessage) {
                                                                                        const proceed = await confirmAction({
                                                                                            title: 'Are you sure?',
                                                                                            text: action.confirmMessage, // Use the confirmMessage from action
                                                                                            icon: 'warning',
                                                                                            confirmButtonText: 'Yes',
                                                                                            cancelButtonText: 'No',
                                                                                        });

                                                                                        if (proceed) {
                                                                                            action.onClick(row); // Proceed if confirmed
                                                                                        }
                                                                                    } else {
                                                                                        action.onClick(row); // No confirmation needed
                                                                                    }
                                                                                }}
                                                                                className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 w-full text-left"
                                                                            >
                                                                                {action.icon && <span
                                                                                    className="mr-2">{action.icon}</span>}
                                                                                {action.label}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                            </React.Fragment>
                        );
                    })
                )}
                </tbody>
            </table>
            {!isLoading && data.length > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={onPageChange}
                    pageSize={pageSize}
                    onPageSizeChange={onPageSizeChange}
                />
            )}
        </div>
    );
};

export default Table;
